import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Heart, ShoppingCart, Minus, Plus, ArrowLeft, Check } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ProductDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addToCart } = useCart();
  const { isInWishlist, addToWishlist, removeFromWishlist } = useWishlist();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);

  const inWishlist = product ? isInWishlist(product.id) : false;

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const { data } = await axios.get(`${API}/products/${id}`);
        setProduct(data);
      } catch (error) {
        console.error('Failed to fetch product:', error);
        toast.error('Product not found');
        navigate('/products');
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [id, navigate]);

  const handleAddToCart = async () => {
    if (!user) {
      toast.error('Please login to add items to cart');
      navigate('/login');
      return;
    }

    const result = await addToCart(product.id, quantity);
    if (result.success) {
      toast.success(`${product.name} added to cart`);
    } else {
      toast.error(result.error);
    }
  };

  const handleWishlistToggle = async () => {
    if (!user) {
      toast.error('Please login to add items to wishlist');
      return;
    }

    if (inWishlist) {
      await removeFromWishlist(product.id);
      toast.success('Removed from wishlist');
    } else {
      await addToWishlist(product.id);
      toast.success('Added to wishlist');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="skeleton h-96 rounded-xl" />
            <div className="space-y-4">
              <div className="skeleton h-8 w-3/4" />
              <div className="skeleton h-6 w-1/4" />
              <div className="skeleton h-24 w-full" />
              <div className="skeleton h-12 w-1/2" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) return null;

  return (
    <div className="min-h-screen bg-[#050505] py-8" data-testid="product-detail-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center space-x-2 text-[#A1A7B5] hover:text-white mb-8 transition-colors"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Back to Products</span>
        </button>

        {/* Product Details */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-12">
          {/* Product Image */}
          <div className="relative">
            <img
              src={product.image_url || 'https://images.pexels.com/photos/5922101/pexels-photo-5922101.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940'}
              alt={product.name}
              className="w-full h-auto rounded-xl"
              data-testid="product-image"
            />
            <button
              onClick={handleWishlistToggle}
              className={`absolute top-4 right-4 p-3 rounded-full transition-all duration-200 ${
                inWishlist ? 'bg-[#00F0FF] text-black' : 'bg-black/50 text-white hover:bg-black/70'
              }`}
              data-testid="wishlist-btn"
            >
              <Heart className={`w-6 h-6 ${inWishlist ? 'fill-current' : ''}`} />
            </button>
          </div>

          {/* Product Info */}
          <div className="space-y-6">
            <div>
              <span className="text-[#00F0FF] text-sm font-medium uppercase tracking-wider">
                Research
              </span>
              <h1 
                className="text-3xl sm:text-4xl font-bold text-white mt-2"
                style={{ fontFamily: 'Outfit, sans-serif' }}
                data-testid="product-name"
              >
                {product.name}
              </h1>
            </div>

            <p className="text-[#A1A7B5] text-lg" data-testid="product-description">
              {product.description}
            </p>

            {/* Specs */}
            <div className="grid grid-cols-2 gap-4 p-4 glass-card">
              {product.purity && (
                <div>
                  <span className="text-[#A1A7B5] text-sm">Purity</span>
                  <p className="text-white font-semibold">{product.purity}</p>
                </div>
              )}
              {product.molecular_weight && (
                <div>
                  <span className="text-[#A1A7B5] text-sm">Molecular Weight</span>
                  <p className="text-white font-semibold">{product.molecular_weight}</p>
                </div>
              )}
              <div>
                <span className="text-[#A1A7B5] text-sm">Stock</span>
                <p className={`font-semibold ${product.stock > 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {product.stock > 0 ? `${product.stock} units` : 'Out of Stock'}
                </p>
              </div>
            </div>

            {/* Tiered Pricing */}
            <div className="glass-card p-5" data-testid="tiered-pricing">
              <p className="text-[#A1A7B5] text-xs uppercase tracking-wider mb-3">Volume Pricing</p>
              <div className="grid grid-cols-3 gap-2">
                <button
                  onClick={() => { if (quantity < 5 || quantity >= 10) setQuantity(1); }}
                  className={`p-3 rounded-lg text-center transition-all border ${quantity >= 1 && quantity < 5 ? 'border-[#00F0FF] bg-[#00F0FF]/10' : 'border-[#1E2532] bg-[#111621] hover:border-[#00F0FF]/40'}`}
                  data-testid="tier-1"
                >
                  <p className="text-[#A1A7B5] text-xs mb-1">1–4 vials</p>
                  <p className="text-white font-bold text-lg">${product.price.toFixed(2)}</p>
                  <p className="text-[#A1A7B5] text-[10px]">per vial</p>
                </button>
                <button
                  onClick={() => setQuantity(5)}
                  className={`p-3 rounded-lg text-center transition-all border ${quantity >= 5 && quantity < 10 ? 'border-[#00F0FF] bg-[#00F0FF]/10' : 'border-[#1E2532] bg-[#111621] hover:border-[#00F0FF]/40'}`}
                  data-testid="tier-2"
                >
                  <p className="text-[#A1A7B5] text-xs mb-1">5–9 vials</p>
                  <p className="text-white font-bold text-lg">${(product.price * 0.95).toFixed(2)}</p>
                  <p className="text-green-400 text-[10px]">Save 5%/vial</p>
                </button>
                <button
                  onClick={() => setQuantity(10)}
                  className={`p-3 rounded-lg text-center transition-all border ${quantity >= 10 ? 'border-[#00F0FF] bg-[#00F0FF]/10' : 'border-[#1E2532] bg-[#111621] hover:border-[#00F0FF]/40'}`}
                  data-testid="tier-3"
                >
                  <p className="text-[#A1A7B5] text-xs mb-1">10+ vials</p>
                  <p className="text-white font-bold text-lg">${(product.price * 0.90).toFixed(2)}</p>
                  <p className="text-green-400 text-[10px]">Save 10%/vial</p>
                </button>
              </div>
            </div>

            {/* Price Display */}
            <div className="flex items-center justify-between">
              <div>
                <span className="text-3xl font-bold text-white" data-testid="product-price">
                  ${(quantity >= 10 ? (product.price * 0.90) : quantity >= 5 ? (product.price * 0.95) : product.price).toFixed(2)}
                </span>
                <span className="text-[#A1A7B5] text-sm ml-2">/ vial</span>
                {quantity > 1 && (
                  <p className="text-[#00F0FF] text-sm mt-1" data-testid="total-price">
                    Total: ${((quantity >= 10 ? (product.price * 0.90) : quantity >= 5 ? (product.price * 0.95) : product.price) * quantity).toFixed(2)}
                    {quantity >= 5 && (
                      <span className="text-green-400 ml-2">
                        (You save ${((quantity >= 10 ? product.price * 0.10 : product.price * 0.05) * quantity).toFixed(2)})
                      </span>
                    )}
                  </p>
                )}
              </div>
            </div>

            {/* Quantity & Add to Cart */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex items-center space-x-4 glass-card p-2">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="p-2 bg-[#1E2532] rounded hover:bg-[#00F0FF]/20 transition-colors"
                  data-testid="decrease-qty"
                >
                  <Minus className="w-5 h-5 text-white" />
                </button>
                <span className="text-white font-semibold w-12 text-center" data-testid="quantity">
                  {quantity}
                </span>
                <button
                  onClick={() => setQuantity(Math.min(product.stock, quantity + 1))}
                  className="p-2 bg-[#1E2532] rounded hover:bg-[#00F0FF]/20 transition-colors"
                  data-testid="increase-qty"
                >
                  <Plus className="w-5 h-5 text-white" />
                </button>
              </div>

              <Button
                onClick={handleAddToCart}
                disabled={product.stock === 0}
                className="btn-primary flex-1 flex items-center justify-center space-x-2"
                data-testid="add-to-cart-btn"
              >
                <ShoppingCart className="w-5 h-5" />
                <span>Add to Cart</span>
              </Button>
            </div>

            {/* Features */}
            <div className="space-y-2">
              <div className="flex items-center space-x-2 text-[#A1A7B5]">
                <Check className="w-5 h-5 text-[#00F0FF]" />
                <span>Third-party HPLC tested</span>
              </div>
              <div className="flex items-center space-x-2 text-[#A1A7B5]">
                <Check className="w-5 h-5 text-[#00F0FF]" />
                <span>99%+ purity guaranteed</span>
              </div>
              <div className="flex items-center space-x-2 text-[#A1A7B5]">
                <Check className="w-5 h-5 text-[#00F0FF]" />
                <span>For research use only — not for human consumption</span>
              </div>
            </div>

            {/* Purity Guarantee */}
            <div className="glass-card p-5 border border-[#1E2532]" data-testid="purity-guarantee">
              <p className="text-[#00F0FF] text-xs font-semibold uppercase tracking-wider mb-2">99% Purity Guaranteed</p>
              <p className="text-[#A1A7B5] text-sm leading-relaxed mb-4">
                We continuously conduct HPLC testing on all of our raw powders as well as finished products to ensure the highest quality. You can have any product purchased from us tested at any HPLC licensed testing facility — and if the results are negative, we will refund the following:
              </p>
              <div className="flex items-center gap-4">
                <div className="bg-[#111621] border border-[#1E2532] rounded-lg px-4 py-2 text-center">
                  <p className="text-[#00F0FF] text-[10px] font-bold uppercase">HPLC Test Fee</p>
                  <p className="text-white font-bold text-xl" style={{ fontFamily: 'Outfit, sans-serif' }}>$100</p>
                </div>
                <span className="text-[#00F0FF] font-bold text-lg">+</span>
                <div className="bg-[#111621] border border-[#1E2532] rounded-lg px-4 py-2 text-center">
                  <p className="text-[#00F0FF] text-[10px] font-bold uppercase">Full Refund</p>
                  <p className="text-white font-bold text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Order Total</p>
                </div>
              </div>
            </div>

            {/* Research Disclaimer */}
            <div className="p-4 bg-[#111621] border border-[#1E2532] rounded-lg">
              <p className="text-[#A1A7B5] text-xs leading-relaxed">
                This product is a research compound intended strictly for laboratory and scientific research purposes only. It is not intended for human consumption, medical, diagnostic, or therapeutic use. By purchasing this product, you confirm that you are a qualified researcher familiar with the safe handling of research materials and will use this product in compliance with all applicable laws and regulations.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetailPage;
